// libraries
import React, { useState, useEffect, useRef } from "react";
import _ from "lodash";
import { AiOutlinePlus } from "react-icons/ai";
import moment from "moment";
import { BiFilterAlt } from "react-icons/bi";
import { useSelector } from "react-redux";
// components
import AppPagesLayout from "components/app-pages-layout";
import AppBreadcrumb from "components/app-breadcrumb";
import AppTabLink from "components/app-tab-link";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppReactTable from "components/app-react-table";
import AppPaginate from "components/app-paginate";
import AppInputWithLabel from "components/app-input-with-label";
import AppDropdown from "components/app-dropdown";
import AppInputDate from "components/app-input-date";
import appToast from "components/app-toast";
import AppDropdownCheckbox from "components/app-dropdown-checkbox";
import AppDualColumnsModal from "components/app-dual-columns-modal";
import AppCheckbox from "components/app-checkbox";
import AppTextArea from "components/app-text-area";
import AppTimePicker from "components/app-time-picker";
import AppModalConfirmation from "components/app-modal-confirmation";
import AppTimePicker2 from "components/app-time-picker-2";
// pages
import PageCreateCoachingSession from "pages/admin/coaching-session/create-coaching-session";
// service
import api from "services/api";
// common
import { sanitizeError, statusColor } from "common/utilities";
// hooks
import usePrevious from "hooks/use-previous";
// route
import employeePathnames from "routes/employee-pathnames";
// hooks
import useIsMount from "hooks/use-is-mount";
// assets
import iconSearch from "assets/images/components/app-input/icon-search.svg";
import iconEdit from "assets/images/icon-edit.svg";
import iconTailedArrowUp from "assets/images/icon-tailed-arrow-up.svg";
import iconSetting from "assets/images/icon-setting.svg";

const appTabLink = [
    {
        label: "Upcoming Schedule",
        pathname: employeePathnames.pageEmployeeUpcomingSchedule,
    },
    {
        label: "Coaching History",
        pathname: employeePathnames.pageEmployeeCoachingHistory,
    },
];

const breadcrumb = [
    {
        label: "Coaching Sessions",
    },
    {
        label: "Upcoming Schedule",
    },
];

const statusOptions = [
    {
        label: "Overdue",
        value: "overdue",
    },
    {
        label: "Upcoming",
        value: "upcoming,",
    },
];

const advanceSearch = [
    {
        name: "startDate",
        placeholder: "Date",
        type: "date",
    },
    {
        name: "startTime",
        placeholder: "Start time",
        type: "time",
    },
    {
        name: "endTime",
        placeholder: "End time",
        type: "time",
    },
    {
        name: "status",
        placeholder: "Status",
        type: "dropdown",
        dropdownOptions: statusOptions,
    },
];

const tableStatusFilter = [
    { label: "Overdue", name: "overdue", value: "overdue" },
    { label: "Upcoming", name: "upcoming", value: "upcoming" },
];

let statusToFilter = [];

const getTableStatusClassNames = (status) => {
    const classNames = ["react-table__status-data"];
    if (status.toLowerCase() === "upcoming") classNames.push("react-table__status--orange");
    if (status.toLowerCase() === "overdue") classNames.push("react-table__status--red");

    return classNames.join(" ");
};

const initialValues = {
    mentorName: {
        placeholder: "Mentor Username",
        value: "",
        error: "",
    },
    mentorId: {
        placeholder: "Mentor ID",
        value: "",
        error: "",
    },
    remark: {
        placeholder: "Remark",
        value: "",
        error: "",
        field: "optional",
    },
    scheduleDate: {
        placeholder: "Schedule date",
        value: "",
        error: "",
    },
    startTime: {
        placeholder: "Start time",
        value: "",
        error: "",
    },
    endTime: {
        placeholder: "End time",
        value: "",
        error: "",
    },
    id: {
        placeholder: "",
        value: "",
        error: "",
    },
    status: {
        placeholder: "",
        value: "",
        error: "",
    },
    staffId: {
        value: "",
    },
};

const statusFilterInitialValues = {
    upcoming: false,
    overdue: false,
};

const PageEmployeeUpcomingSession = () => {
    const profile = useSelector((state) => state.auth);
    const [tableSearchFilter, setTableSearchFilter] = useState({
        pageNo: 0,
        pageSize: 5,
        searchValue: "",
        sortBy: "",
        sortDir: "asc",
        status: "",
        staffId: profile.staffID,
    });
    const [detailSearch, setDetailSearch] = useState({
        staffId: profile.staffID,
        startDate: "",
        startTime: "",
        endTime: "",
        pageNo: 0,
        pageSize: 5,
        sortBy: "",
        status: "",
    });
    const [advanceSearchValue, setAdvanceSearchValue] = useState({
        startDate: "",
        startTime: "",
        endTime: "",
    });
    const [tableData, setTableData] = useState({
        content: [],
        last: null,
        pageNo: 0,
        pageSize: 5,
        totalElements: null,
        totalPages: 1,
    });
    const prevValueSortHeader = usePrevious({
        sortBy: tableSearchFilter.sortBy,
        sortDir: tableSearchFilter.sortDir,
    });
    const prevAdvanceValueSortHeader = usePrevious({
        sortBy: detailSearch.sortBy,
        sortDir: detailSearch.sortDir,
    });
    const prevDetailSearch = usePrevious(detailSearch);
    const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
    const searchKeywordInputRef = useRef(null);
    const startTimeKeywordInputRef = useRef(null);
    const endTimeKeywordInputRef = useRef(null);
    const [statusFilterOpen, setStatusFilterOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState(statusFilterInitialValues);
    const [editLargeModalIsOpen, setEditLargeModalIsOpen] = useState(false);
    const [timeIsOpen, setTimeIsOpen] = useState("");
    const [createSessionIsOpen, setCreateSessionIsOpen] = useState(false);
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        confirmDisable: false,
    });
    const [mentorNameList, setMentorNameList] = useState([]);
    const [selectedSchedule, setSelectedSchedule] = useState(initialValues);
    const [selectedScheduleExtraInfo, setSelectedScheduleExtraInfo] = useState({
        status: "",
        staffName: "",
    });
    const isMount = useIsMount();

    const getTableData = async (payload) => {
        try {
            const response = await api.get.coachingSessionUpcomingSchedule(payload);
            setTableData(response.data.result);
        } catch (error) {
            let sanitizedError = sanitizeError(error);
            appToast(sanitizedError, false);
        }
    };

    useEffect(() => {
        getTableData(tableSearchFilter);
    }, [tableSearchFilter]);

    const getTableAdvanceSearchData = async (payload) => {
        try {
            const response = await api.get.coachingSessionUpcomingScheduleAdvanceSearch(payload);
            setTableData(response.data.result);
        } catch (error) {
            let sanitizedError = sanitizeError(error);
            appToast(sanitizedError, false);
        }
    };

    useEffect(() => {
        if (!isMount && prevDetailSearch !== detailSearch) {
            getTableAdvanceSearchData(detailSearch);
        }
    }, [isMount, detailSearch, prevDetailSearch]);

    useEffect(() => {
        const getStaffNamePosition = async () => {
            try {
                const response = await api.get.getStaffNamePosition();
                const mentorNameListUpdate = response.data.result.staffNameList.map((o) => {
                    return {
                        label: o.userName,
                        value: o.value2,
                    };
                });
                setMentorNameList(mentorNameListUpdate);
            } catch (error) {
                const sanitizedError = sanitizeError(error);
                appToast(sanitizedError, false);
            }
        };
        getStaffNamePosition();
    }, []);

    const headerArrowRotate = (active) => {
        const classNames = ["react-table__arrow-icon"];
        if (active && tableSearchFilter.sortDir === "desc") classNames.push("react-table__arrow-icon-down");
        if (active && detailSearch.sortDir === "desc") classNames.push("react-table__arrow-icon-down");
        if (active) classNames.push("react-table__arrow-icon--active");

        return classNames.join(" ");
    };

    const headerFilterClick = (header) => {
        let column = header.column;
        if (showAdvanceSearch) {
            if (prevAdvanceValueSortHeader && prevAdvanceValueSortHeader.sortBy === column.searchFilterValue) {
                if (prevAdvanceValueSortHeader.sortDir === "asc") {
                    setDetailSearch({
                        ...detailSearch,
                        sortBy: column.searchFilterValue,
                        sortDir: "desc",
                        pageNo: 0,
                    });
                } else {
                    setDetailSearch({
                        ...detailSearch,
                        sortBy: column.searchFilterValue,
                        sortDir: "asc",
                        pageNo: 0,
                    });
                }
            } else {
                setDetailSearch({
                    ...detailSearch,
                    sortBy: column.searchFilterValue,
                    sortDir: "asc",
                    pageNo: 0,
                });
            }
        } else {
            if (prevValueSortHeader && prevValueSortHeader.sortBy === column.searchFilterValue) {
                if (prevValueSortHeader.sortDir === "asc") {
                    setTableSearchFilter({
                        ...tableSearchFilter,
                        sortBy: column.searchFilterValue,
                        sortDir: "desc",
                        pageNo: 0,
                    });
                } else {
                    setTableSearchFilter({
                        ...tableSearchFilter,
                        sortBy: column.searchFilterValue,
                        sortDir: "asc",
                        pageNo: 0,
                    });
                }
            } else {
                setTableSearchFilter({
                    ...tableSearchFilter,
                    sortBy: column.searchFilterValue,
                    sortDir: "asc",
                    pageNo: 0,
                });
            }
        }
    };

    const tableColumn = [
        {
            id: "staffUserName",
            Header: (header) => {
                return (
                    <span onClick={() => headerFilterClick(header)} className="react-table__attached-benefit-header react-table__header">
                        Employee Username
                        <img
                            className={showAdvanceSearch ? headerArrowRotate(detailSearch.sortBy === header.column.searchFilterValue) : headerArrowRotate(tableSearchFilter.sortBy === header.column.searchFilterValue)}
                            src={iconTailedArrowUp}
                            alt=""
                        />
                    </span>
                );
            },
            Cell: (row) => {
                return <span className="react-table__attached-benefit-data">{row.row.original.staffUserName}</span>;
            },
            maxWidth: 140,
            searchFilterValue: "staff_username",
        },
        {
            id: "coachUserName",
            Header: (header) => {
                return (
                    <span onClick={() => headerFilterClick(header)} className="react-table__header">
                        Mentor Username
                        <img
                            className={showAdvanceSearch ? headerArrowRotate(detailSearch.sortBy === header.column.searchFilterValue) : headerArrowRotate(tableSearchFilter.sortBy === header.column.searchFilterValue)}
                            src={iconTailedArrowUp}
                            alt=""
                        />
                    </span>
                );
            },
            maxWidth: 140,
            accessor: "coachUserName",
            searchFilterValue: "coach_username",
        },
        {
            id: "coachingDate",
            Header: (header) => {
                return (
                    <span onClick={() => headerFilterClick(header)} className="react-table__header">
                        Date
                        <img
                            className={showAdvanceSearch ? headerArrowRotate(detailSearch.sortBy === header.column.searchFilterValue) : headerArrowRotate(tableSearchFilter.sortBy === header.column.searchFilterValue)}
                            src={iconTailedArrowUp}
                            alt=""
                        />
                    </span>
                );
            },
            maxWidth: 140,
            accessor: "coachingDate",
            searchFilterValue: "coaching_date",
        },
        {
            id: "time",
            Header: (header) => {
                return (
                    <span onClick={() => headerFilterClick(header)} className="react-table__header">
                        Time
                        <img
                            className={showAdvanceSearch ? headerArrowRotate(detailSearch.sortBy === header.column.searchFilterValue) : headerArrowRotate(tableSearchFilter.sortBy === header.column.searchFilterValue)}
                            src={iconTailedArrowUp}
                            alt=""
                        />
                    </span>
                );
            },
            maxWidth: 140,
            accessor: "time",
            searchFilterValue: "start_time",
        },
        {
            id: "status",
            Header: () => {
                const getStatusClassNames = (statusFilterOpen) => {
                    const classNames = ["react-table__icon-filter"];
                    if (statusFilterOpen) classNames.push("react-table__icon-filter--active");
                    return classNames.join(" ");
                };
                return (
                    <div onClick={(e) => e.stopPropagation()}>
                        <span className="react-table__status-header" onClick={() => setStatusFilterOpen(!statusFilterOpen)}>
                            Status
                            <BiFilterAlt className={getStatusClassNames(statusFilterOpen)} />
                        </span>
                        {statusFilterOpen ? (
                            <div className="react-table__dropdown">
                                <AppDropdownCheckbox
                                    dropdownList={tableStatusFilter}
                                    onChange={(item, value) => {
                                        setStatusFilter({
                                            ...statusFilter,
                                            [item.name]: value,
                                        });
                                        if (value) {
                                            statusToFilter.push(item.value);
                                        } else {
                                            statusToFilter = statusToFilter.filter((e) => e !== item.value);
                                        }
                                        if (showAdvanceSearch) {
                                            if (statusToFilter.length === 1) {
                                                setDetailSearch({
                                                    ...detailSearch,
                                                    status: statusToFilter[0],
                                                    pageNo: 0,
                                                });
                                            } else {
                                                setDetailSearch({
                                                    ...detailSearch,
                                                    status: "",
                                                    pageNo: 0,
                                                });
                                            }
                                        } else {
                                            if (statusToFilter.length === 1) {
                                                setTableSearchFilter({
                                                    ...tableSearchFilter,
                                                    status: statusToFilter[0],
                                                    pageNo: 0,
                                                });
                                            } else {
                                                setTableSearchFilter({
                                                    ...tableSearchFilter,
                                                    status: "",
                                                    pageNo: 0,
                                                });
                                            }
                                        }
                                    }}
                                    state={statusFilter}
                                />
                            </div>
                        ) : null}
                    </div>
                );
            },
            Cell: (row) => {
                return (
                    <div className={getTableStatusClassNames(row.row.original.status)}>
                        <span>{row.row.original.status}</span>
                    </div>
                );
            },
            // searchFilterValue: "status",
            width: 120,
        },
        {
            id: "button",
            Cell: () => {
                return <img className="react-table__edit-icon" src={iconEdit} alt="" />;
            },
            maxWidth: 30,
        },
    ];

    const searchKeywordTyped = (value) => {
        setTableSearchFilter({
            pageNo: 0,
            pageSize: 5,
            searchValue: value,
            sortBy: "",
            sortDir: "asc",
            status: "",
            staffId: profile.staffID,
        });
    };

    const searchBarOnChange = (e) => {
        searchKeywordTyped(e.target.value);
    };

    const handleKeypress = (e) => {
        if (e.code === "Enter") {
            searchKeywordTyped(e.target.value);
        }
    };

    const onPageChange = (e) => {
        if (showAdvanceSearch) {
            setDetailSearch({
                ...detailSearch,
                pageNo: e.selected,
            });
        } else {
            setTableSearchFilter({
                ...tableSearchFilter,
                pageNo: e.selected,
            });
        }
    };

    const applySearchKeywordSearch = async () => {
        let payload = {
            searchValue: "",
            ...tableSearchFilter,
        };
        getTableData(payload);
    };

    const applyAdvanceSearch = async () => {
        setStatusFilter(statusFilterInitialValues);
        setDetailSearch({
            ...detailSearch,
            startDate: advanceSearchValue.startDate,
            startTime: advanceSearchValue.startTime,
            endTime: advanceSearchValue.endTime,
            pageNo: 0,
            pageSize: 5,
            sortBy: "",
            status: "",
        });
    };

    const onRowSelect = async (row) => {
        try {
            const response = await api.get.coachingSessionSessionDetail(row.id);
            let result = response.data.result;
            result = { ...result, id: row.id };
            let initialValuesCloned = { ...initialValues };
            for (const [k, v] of Object.entries(result)) {
                if (Object.keys(initialValuesCloned).includes(k)) {
                    initialValuesCloned = {
                        ...initialValuesCloned,
                        [k]: {
                            ...initialValuesCloned[k],
                            value: v,
                        },
                    };
                }
            }
            setSelectedSchedule(initialValuesCloned);
            setSelectedScheduleExtraInfo({
                status: result.status,
                staffName: result.staffName,
            });
            setEditLargeModalIsOpen(true);
        } catch (error) {
            let sanitizedError = sanitizeError(error);
            appToast(sanitizedError, false);
        }
    };

    const upComingSessionEditSave = () => {
        let selectedScheduleCloned = { ...selectedSchedule };
        for (const key in selectedScheduleCloned) {
            if (!selectedScheduleCloned[key].value && selectedScheduleCloned[key].field !== "optional") {
                selectedScheduleCloned = {
                    ...selectedScheduleCloned,
                    [key]: {
                        ...selectedScheduleCloned[key],
                        error: `${selectedScheduleCloned[key].placeholder} is required.`,
                    },
                };
            } else if (selectedScheduleCloned[key].value === "Invalid date") {
                selectedScheduleCloned = {
                    ...selectedScheduleCloned,
                    [key]: {
                        ...selectedScheduleCloned[key],
                        error: `${selectedScheduleCloned[key].placeholder} is invalid.`,
                    },
                };
            } else {
                selectedScheduleCloned = {
                    ...selectedScheduleCloned,
                    [key]: {
                        ...selectedScheduleCloned[key],
                        error: "",
                    },
                };
            }
        }
        let endTime = moment(selectedScheduleCloned.endTime.value, "hh:mm A");
        let startTime = moment(selectedScheduleCloned.startTime.value, "hh:mm A");
        if (endTime.isBefore(startTime) || endTime._i === startTime._i) {
            selectedScheduleCloned = {
                ...selectedScheduleCloned,
                endTime: {
                    ...selectedScheduleCloned.endTime,
                    error: "End time is required to be after start time.",
                },
                startTime: {
                    ...selectedScheduleCloned.startTime,
                    error: "Start time is required to be before end time.",
                },
            };
        }
        setSelectedSchedule(selectedScheduleCloned);

        if (Object.values(selectedScheduleCloned).every((e) => e.error === "")) {
            setEditLargeModalIsOpen(false);
            setConfirmModal({
                isOpen: true,
                confirmDisable: false,
            });
        }
    };

    const editSaveConfirmed = async () => {
        let selectedScheduleCloned = { ...selectedSchedule };
        let payload = {};
        for (const [k, v] of Object.entries(selectedScheduleCloned)) {
            payload = {
                ...payload,
                [k]: v.value,
            };
        }
        setConfirmModal({
            ...confirmModal,
            confirmDisable: true,
        });
        try {
            const { mentorName, ...removedMentorName } = payload;
            await api.post.coachingSessionEditSession(removedMentorName);
            setConfirmModal({
                isOpen: false,
                confirmDisable: false,
            });
            appToast("Coaching session has been updated successfully", true);
            getTableData(tableSearchFilter);
        } catch (error) {
            appToast(sanitizeError(error), false);
            setConfirmModal({
                ...confirmModal,
                confirmDisable: false,
            });
        }
    };

    return (
        <AppPagesLayout>
            <div
                className="page-coaching-upcoming"
                onClick={() => {
                    setStatusFilterOpen(false);
                    setTimeIsOpen("");
                }}>
                <AppModalConfirmation
                    isOpenModal={confirmModal.isOpen}
                    onRequestClose={() => {
                        setConfirmModal({
                            isOpen: false,
                            confirmDisable: false,
                        });
                        setEditLargeModalIsOpen(true);
                    }}
                    details="Confirm to save"
                    onClick={editSaveConfirmed}
                    buttonDisabled={confirmModal.confirmDisable}
                    buttonLabel="Confirm"
                />
                <PageCreateCoachingSession largeModalIsOpen={createSessionIsOpen} onRequestClose={() => setCreateSessionIsOpen(false)} onRequestOpen={() => setCreateSessionIsOpen(true)} postSuccess={() => getTableData(tableSearchFilter)} />

                <AppDualColumnsModal
                    title="Schedule a Session"
                    isOpenModal={editLargeModalIsOpen}
                    onRequestClose={() => setEditLargeModalIsOpen(false)}
                    status={selectedSchedule.status.value}
                    statusColor={statusColor(selectedSchedule.status.value)}
                    onClick={() => {
                        setTimeIsOpen("");
                    }}
                    childrenLeft={
                        <div className="page-coaching-upcoming__column-modal-left">
                            <div className="column-modal-left__checkbox-wrapper">
                                <AppCheckbox
                                    onChange={() => {
                                        if (selectedSchedule.status.value && selectedSchedule.status.value.toLowerCase() === "completed") {
                                            setSelectedSchedule({
                                                ...selectedSchedule,
                                                status: {
                                                    ...selectedSchedule.status,
                                                    value: "",
                                                },
                                            });
                                        } else {
                                            setSelectedSchedule({
                                                ...selectedSchedule,
                                                status: {
                                                    ...selectedSchedule.status,
                                                    value: "Completed",
                                                },
                                            });
                                        }
                                    }}
                                    checked={selectedSchedule.status.value && selectedSchedule.status.value.toLowerCase() === "completed" ? true : false}
                                />
                                <span className="column-modal-left__checkbox-label">Mark as Completed</span>
                            </div>
                            <div className="column-modal-left__input-wrapper">
                                <AppInputWithLabel placeholder="Employee Username" value={selectedScheduleExtraInfo.staffName} disabled={true} />
                            </div>
                            <div className="column-modal-left__input-wrapper">
                                <AppDropdown
                                    placeholder="Mentor Username"
                                    onChange={(selected) => {
                                        setSelectedSchedule({
                                            ...selectedSchedule,
                                            mentorName: {
                                                ...selectedSchedule.mentorName,
                                                value: selected.label,
                                                error: "",
                                            },
                                            mentorId: {
                                                ...selectedSchedule.mentorId,
                                                value: selected.value,
                                                error: "",
                                            },
                                        });
                                    }}
                                    value={selectedSchedule.mentorName.value}
                                    dropdownOptions={JSON.parse(JSON.stringify(mentorNameList))}
                                    // currentInputValue={selectedSchedule.mentorName.value ? mentorNameList.filter((ele) => ele.label === selectedSchedule.mentorName.value)[0] : null}
                                    error={selectedSchedule.mentorName.error}
                                    isDisabled={selectedScheduleExtraInfo.status.toLowerCase() === "overdue" ? true : false}
                                />
                            </div>
                            <div className="column-modal-left__input-wrapper">
                                <AppTextArea
                                    placeholder="Remark"
                                    onChange={(e) =>
                                        setSelectedSchedule({
                                            ...selectedSchedule,
                                            remark: {
                                                ...selectedSchedule.remark,
                                                value: e.target.value,
                                                error: "",
                                            },
                                        })
                                    }
                                    value={selectedSchedule.remark.value}
                                    error={selectedSchedule.remark.error}
                                    disabled={selectedScheduleExtraInfo.status.toLowerCase() === "overdue" ? true : false}
                                />
                            </div>
                        </div>
                    }
                    childrenRight={
                        <div className="page-coaching-upcoming__column-modal-right">
                            <div className="column-modal-right__cancel-wrapper">
                                <button
                                    className="column-modal-right__cancel"
                                    onClick={() => {
                                        if (selectedScheduleExtraInfo.status && selectedScheduleExtraInfo.status.toLowerCase() === "cancelled") {
                                            setSelectedSchedule({
                                                ...selectedSchedule,
                                                status: {
                                                    ...selectedSchedule.status,
                                                    value: selectedScheduleExtraInfo.status,
                                                },
                                            });
                                        } else {
                                            setSelectedSchedule({
                                                ...selectedSchedule,
                                                status: {
                                                    ...selectedSchedule.status,
                                                    value: "Cancelled",
                                                },
                                            });
                                        }
                                    }}>
                                    Cancel Session
                                </button>
                            </div>
                            <div className="column-modal-right__input-wrapper">
                                <AppInputDate
                                    placeholder="Date"
                                    onChange={(value) =>
                                        setSelectedSchedule({
                                            ...selectedSchedule,
                                            scheduleDate: {
                                                ...selectedSchedule.scheduleDate,
                                                value: value,
                                                error: "",
                                            },
                                        })
                                    }
                                    value={selectedSchedule.scheduleDate.value}
                                    error={selectedSchedule.scheduleDate.error}
                                    disabled={selectedScheduleExtraInfo.status.toLowerCase() === "overdue" ? true : false}
                                />
                            </div>
                            <div onClick={(e) => e.stopPropagation()} className="column-modal-right__time-wrapper">
                                <AppTimePicker
                                    placeholder="Start Time"
                                    onFocus={(e, placeholder) => {
                                        setTimeIsOpen(placeholder);
                                    }}
                                    timeIsOpenPlaceholder={timeIsOpen}
                                    touched={true}
                                    startTime={{
                                        hours: selectedSchedule.startTime.value ? selectedSchedule.startTime.value.split(":")[0] : "",
                                        minutes: selectedSchedule.startTime.value ? selectedSchedule.startTime.value.split(":").join(" ").split(" ")[1] : "",
                                        ampm: selectedSchedule.startTime.value ? selectedSchedule.startTime.value.split(":").join(" ").split(" ")[2] : "",
                                    }}
                                    valueFunc={(value) => {
                                        let timeFormatted = value.hours && value.minutes && value.ampm ? `${value.hours}:${value.minutes} ${value.ampm}` : "";
                                        if (selectedSchedule.startTime.value !== timeFormatted) {
                                            setSelectedSchedule({
                                                ...selectedSchedule,
                                                startTime: {
                                                    ...selectedSchedule.startTime,
                                                    value: timeFormatted,
                                                    error: "",
                                                },
                                                endTime: {
                                                    ...selectedSchedule.endTime,
                                                    error: "",
                                                },
                                            });
                                        }
                                    }}
                                    error={selectedSchedule.startTime.error}
                                    disabled={selectedScheduleExtraInfo.status.toLowerCase() === "overdue" ? true : false}
                                />
                            </div>

                            <div onClick={(e) => e.stopPropagation()} className="column-modal-right__time-wrapper">
                                <AppTimePicker
                                    placeholder="End Time"
                                    onFocus={(e, placeholder) => {
                                        setTimeIsOpen(placeholder);
                                    }}
                                    timeIsOpenPlaceholder={timeIsOpen}
                                    startTime={{
                                        hours: selectedSchedule.endTime.value ? selectedSchedule.endTime.value.split(":")[0] : "",
                                        minutes: selectedSchedule.endTime.value ? selectedSchedule.endTime.value.split(":").join(" ").split(" ")[1] : "",
                                        ampm: selectedSchedule.endTime.value ? selectedSchedule.endTime.value.split(":").join(" ").split(" ")[2] : "",
                                    }}
                                    touched={true}
                                    valueFunc={(value) => {
                                        let timeFormatted = `${value.hours}:${value.minutes} ${value.ampm}`;
                                        if (selectedSchedule.endTime.value !== timeFormatted) {
                                            setSelectedSchedule({
                                                ...selectedSchedule,
                                                endTime: {
                                                    ...selectedSchedule.endTime,
                                                    value: timeFormatted,
                                                    error: "",
                                                },
                                                startTime: {
                                                    ...selectedSchedule.startTime,
                                                    error: "",
                                                },
                                            });
                                        }
                                    }}
                                    error={selectedSchedule.endTime.error}
                                    disabled={selectedScheduleExtraInfo.status.toLowerCase() === "overdue" ? true : false}
                                />
                            </div>
                        </div>
                    }
                    childrenBottom={
                        <div className="page-coaching-upcoming__column-modal-bottom">
                            <div className="column-modal-bottom__modal-input-button">
                                <AppButton label="Cancel" size="l" buttonType="outline" onClick={() => setEditLargeModalIsOpen(false)} />
                            </div>
                            <div className="column-modal-bottom__modal-input-button">
                                <AppButton label="Save" size="l" onClick={upComingSessionEditSave} />
                            </div>
                        </div>
                    }
                />
                <div className="page-coaching-upcoming__breadcrumb">
                    <AppBreadcrumb list={breadcrumb} />
                </div>
                <div className="page-coaching-upcoming__tab-wrapper">
                    <AppTabLink tabList={appTabLink} />
                </div>

                <div className="page-coaching-upcoming__row">
                    <div className="page-coaching-upcoming__searchBar-wrapper">
                        <AppInput
                            leftSrc={iconSearch}
                            placeholder="Search keyword"
                            onChange={_.debounce((e) => searchBarOnChange(e), 1000)}
                            onKeyPress={handleKeypress}
                            rightSrc={iconSetting}
                            rightSrcOnClick={() => {
                                setStatusFilter({
                                    upcoming: false,
                                    overdue: false,
                                });
                                statusToFilter = [];
                                setShowAdvanceSearch(!showAdvanceSearch);
                                searchKeywordInputRef.current.value = "";
                                searchKeywordTyped("");
                            }}
                            onFocus={() => {
                                if (showAdvanceSearch) {
                                    setShowAdvanceSearch(false);
                                    searchKeywordInputRef.current.value = "";
                                    searchKeywordTyped("");
                                    applySearchKeywordSearch();
                                    setStatusFilter({
                                        upcoming: false,
                                        overdue: false,
                                    });
                                    statusToFilter = [];
                                }
                            }}
                            ref={searchKeywordInputRef}
                        />
                    </div>
                    <div className="page-coaching-upcoming__button-wrapper">
                        <AppButton size="s" buttonIcon={<AiOutlinePlus size={12} />} label="Schedule Coaching Session" onClick={() => setCreateSessionIsOpen(true)} />
                    </div>
                </div>

                {showAdvanceSearch ? (
                    <div className="page-coaching-upcoming__search-content">
                        <div className="page-coaching-upcoming__input">
                            {advanceSearch.map((item, index) => {
                                if (item.type === "input") {
                                    return (
                                        <div key={index} className="page-coaching-upcoming__input-wrapper">
                                            <AppInputWithLabel
                                                placeholder={item.placeholder}
                                                onChange={(e) =>
                                                    setAdvanceSearchValue({
                                                        ...advanceSearchValue,
                                                        [item.name]: e.target.value,
                                                    })
                                                }
                                                value={advanceSearchValue[item.name]}
                                            />
                                        </div>
                                    );
                                } else if (item.type === "time") {
                                    return (
                                        <div key={index} className="page-coaching-upcoming__input-wrapper" onClick={(e) => e.stopPropagation()}>
                                            <AppTimePicker2
                                                placeholder={item.placeholder}
                                                onFocus={(e, placeholder) => {
                                                    setTimeIsOpen(placeholder);
                                                }}
                                                timeIsOpenPlaceholder={timeIsOpen}
                                                fallbackValues={item.name === "startTime" ? "10.00 AM" : item.name === "endTime" ? "11.00 AM" : ""}
                                                valueFunc={(value) => {
                                                    if (advanceSearchValue[item.name] !== value) {
                                                        setAdvanceSearchValue({
                                                            ...advanceSearchValue,
                                                            [item.name]: value,
                                                        });
                                                    }
                                                }}
                                                ref={item.name === "startTime" ? startTimeKeywordInputRef : item.name === "endTime" ? endTimeKeywordInputRef : null}
                                            />
                                        </div>
                                    );
                                } else if (item.type === "date") {
                                    return (
                                        <div key={index} className="page-coaching-upcoming__input-wrapper">
                                            <AppInputDate
                                                placeholder={item.placeholder}
                                                onChange={(value) => {
                                                    if (value === "") {
                                                        setAdvanceSearchValue({
                                                            ...advanceSearchValue,
                                                            [item.name]: "",
                                                        });
                                                    } else {
                                                        setAdvanceSearchValue({
                                                            ...advanceSearchValue,
                                                            [item.name]: moment(value, "DD/MM/YYYY").format("YYYY-MM-DD"),
                                                        });
                                                    }
                                                }}
                                                value={moment(advanceSearchValue[item.name], "YYYY-MM-DD").format("DD/MM/YYYY")}
                                            />
                                        </div>
                                    );
                                } else {
                                    return null;
                                }
                            })}
                        </div>
                        <div className="page-coaching-upcoming__advance-button-row">
                            <div className="page-coaching-upcoming__advance-button-wrapper">
                                <AppButton
                                    label="Reset"
                                    buttonType="outline"
                                    size="s"
                                    onClick={() => {
                                        setDetailSearch({
                                            ...detailSearch,
                                            startDate: "",
                                            startTime: "",
                                            endTime: "",
                                            pageNo: 0,
                                            pageSize: 5,
                                            sortBy: "",
                                            status: "",
                                        });
                                        setAdvanceSearchValue({
                                            ...advanceSearchValue,
                                            startDate: "",
                                            startTime: "",
                                            endTime: "",
                                        });
                                        startTimeKeywordInputRef.current.resetValue();
                                        endTimeKeywordInputRef.current.resetValue();
                                    }}
                                />
                            </div>
                            <div className="page-coaching-upcoming__advance-button-wrapper">
                                <AppButton label="Apply" size="s" onClick={applyAdvanceSearch} />
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className="page-coaching-upcoming__table-wrapper">
                    <AppReactTable onClickRow={onRowSelect} columns={tableColumn} data={tableData.content} searchKeyword={tableSearchFilter.searchValue || !Object.values(advanceSearchValue).every((e) => e === "")} />
                </div>
                <div className="page-coaching-upcoming__pagination">
                    <AppPaginate onPageChange={onPageChange} pageCount={tableData.totalPages} forcePage={tableSearchFilter.pageNo} />
                </div>
            </div>
        </AppPagesLayout>
    );
};

export default PageEmployeeUpcomingSession;
